import { Injectable } from '@angular/core';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { Line } from 'src/app/core/models/line.model';
import { SegmentConfig } from 'src/app/core/models/segment-config.model';
import { Simulation } from 'src/app/core/models/simulation.model';
import { SimulationManagerService } from '../../simulation-manager.service';
import { MapsService } from '../maps/maps.service';

@Injectable({
  providedIn: 'root',
})
export class AccessoriesService {
  constructor(
    private simulationService: SimulationManagerService,
    private mapsService: MapsService,
  ) {}

  get simulation(): Simulation {
    return this.simulationService.simulation;
  }

  anyConfIsUsing(conf: any[], id: number): boolean {
    return conf.map((x) => x?.accId === id).reduce((x, y) => x || y, false);
  }

  isObjectInUse(id: number): string[] | null {
    const type = this.mapsService.getTypeById(id);
    const foundInLines = this.simulation.line
      .filter((line) => {
        return this.findUsageByType(id, type, line);
      })
      .map((line) => line.title);
    return foundInLines.length ? foundInLines : null;
  }

  isObjectByIdInUse(id: number): number[] | null {
    const type = this.mapsService.getTypeById(id);
    const foundInLines = this.simulation.line
      .filter((line) => {
        return this.findUsageByType(id, type, line);
      })
      .map((line) => line.internalId);
    return foundInLines.length ? foundInLines : null;
  }

  findUsageByType(id: number, type: string, line: Line): boolean {
    switch (type) {
      case CONSTANTS.ACCESSORY_TYPES.FLOATER:
        return this.findFloater(id, line);
      case CONSTANTS.ACCESSORY_TYPES.STIFFENER:
        return this.findStiffener(id, line);
      case CONSTANTS.ACCESSORY_TYPES.STRAKE:
        return this.findStrake(id, line);
      case CONSTANTS.ACCESSORY_TYPES.FLEXJOINT:
        return this.findFlexJoint(id, line);
      default:
        console.error('Unknown accessory type found: ', type);
        return false;
    }
  }

  findFloater(id: number, line: Line): boolean {
    return !!line.segConfig.find((segConfig: SegmentConfig) => {
      return !!segConfig.floaterConfig.find(({ accId }) => accId === id);
    });
  }

  findStrake(id: number, line: Line): boolean {
    return !!line.segConfig.find((segConfig: SegmentConfig) => {
      return !!segConfig.strakeConfig.find(({ accId }) => accId === id);
    });
  }

  findStiffener(id: number, line: Line): boolean {
    return !!line.segConfig.find((segConfig: SegmentConfig) => {
      const foundAtBegin = segConfig.stiffenerBeginId === id;
      const foundAtEnd = segConfig.stiffenerEndId === id;
      return foundAtBegin || foundAtEnd;
    });
  }

  findFlexJoint(id: number, line: Line): boolean {
    return !!line.segConfig.find((segConfig: SegmentConfig) => {
      const foundAtBegin = segConfig.flexjointBeginId === id;
      const foundAtEnd = segConfig.flexjointEndId === id;
      return foundAtBegin || foundAtEnd;
    });
  }

  isAccessory(type: string): boolean {
    return (
      type === CONSTANTS.ACCESSORY_TYPES.FLOATER ||
      type === CONSTANTS.ACCESSORY_TYPES.STIFFENER ||
      type === CONSTANTS.ACCESSORY_TYPES.STRAKE ||
      type === CONSTANTS.ACCESSORY_TYPES.FLEXJOINT
    );
  }
}
